import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  LIFETIME_CAP_COPY,
  PERIOD_LABEL,
  formatMoney,
  formatRenewal,
} from '@/features/workspace-usage';
import { PeriodType } from '@/lib/api/generated/model';
import type { OrganizationUsageSummary } from '@/lib/api/generated/model';

import { restartsPeriod, workspacesStoppedBy } from '../blockedForecast';
import { parseConcurrencyLimit } from '../concurrencyLimit';
import { useSetConcurrencyLimit } from '../hooks/useConcurrency';
import { useSetDefaultLimit } from '../hooks/useLimits';
import { parseLimitAmount } from '../limitAmount';
import { LimitField } from './LimitField';
import { PeriodField } from './PeriodField';

const EDITOR =
  'border-border bg-card flex shrink-0 flex-col gap-4 rounded-lg border px-4 py-4';

/**
 * The default's period, inferred rather than read (spec 091 research R1): the usage
 * read carries the default's amount only, and every workspace following the default
 * inherits its period — so the first such row says what it is. With none, Monthly.
 */
function inferredDefaultPeriodType(
  summary: OrganizationUsageSummary,
): PeriodType {
  const following = summary.workspaces.find((row) => !row.is_override);
  return following?.period_type ?? PeriodType.monthly;
}

/**
 * The organization's spend default (spec 073 US2, 091), re-homed under its policy card
 * by spec 117 with its rules intact.
 *
 * The warning before saving is the point: lowering the default can stop every
 * workspace that follows it at once, and that is disclosed rather than prevented —
 * halting organization-wide spend is legitimate and usually urgent. So the count
 * appears as the figure is typed, and the button still says save.
 */
export function SpendDefaultEditor({
  summary,
  onDone,
}: {
  summary: OrganizationUsageSummary;
  onDone: () => void;
}) {
  const currency = summary.spend.currency ?? 'USD';
  const [draft, setDraft] = useState(summary.default_limit ?? '');
  const [periodDraft, setPeriodDraft] = useState<PeriodType>(() =>
    inferredDefaultPeriodType(summary),
  );
  const [submitted, setSubmitted] = useState(false);
  const setDefault = useSetDefaultLimit();
  const parsed = parseLimitAmount(draft);
  const error = submitted && !parsed.ok ? parsed.reason : null;
  // The same check whether the amount, the period, or both changed (spec 091). A new
  // period type starts the default's period at zero, so that save stops nothing (238).
  const restarts = restartsPeriod(
    summary.default_limit === null ? null : inferredDefaultPeriodType(summary),
    periodDraft,
  );
  const stopped =
    parsed.ok && !restarts
      ? workspacesStoppedBy(summary.workspaces, parsed.amount, {
          scope: 'default',
        })
      : 0;
  // A period with no amount to attach it to has nothing to save.
  const amountBlank = draft.trim() === '';
  const amountUnchanged = draft.trim() === (summary.default_limit ?? '');
  const periodUnchanged = periodDraft === inferredDefaultPeriodType(summary);
  const unchanged = amountBlank || (amountUnchanged && periodUnchanged);

  return (
    <form
      aria-label="Spend default"
      className={EDITOR}
      onSubmit={(event) => {
        event.preventDefault();
        setSubmitted(true);
        if (!parsed.ok) return;
        setDefault.mutate(
          { amount: parsed.amount, periodType: periodDraft },
          { onSuccess: onDone },
        );
      }}
    >
      <p className="text-muted-foreground text-sm">
        Applies to every workspace without a custom limit. Raising it releases
        every one of them at once; lowering it can stop them.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-48 flex-1">
          <LimitField
            label="Amount per period"
            value={draft}
            onChange={(next) => {
              setDraft(next);
              setSubmitted(false);
            }}
            currency={currency}
            error={error}
          />
        </div>
        <PeriodField value={periodDraft} onChange={setPeriodDraft} />
      </div>
      {parsed.ok ? (
        <p className="text-muted-foreground text-sm">
          Every workspace without a custom limit gets{' '}
          {formatMoney(parsed.amount, currency)}
          {periodDraft === PeriodType.none
            ? `, ${LIFETIME_CAP_COPY.toLowerCase()}.`
            : ` each ${PERIOD_LABEL[periodDraft].toLowerCase()} period, renewing ${formatRenewal(periodDraft)}.`}
        </p>
      ) : null}
      {stopped > 0 && !unchanged ? (
        <p
          role="status"
          className="border-board-state-waiting/40 text-foreground rounded-md border border-dashed px-3 py-2 text-sm"
        >
          This will stop{' '}
          <span className="font-medium">
            {stopped === 1 ? '1 workspace' : `${String(stopped)} workspaces`}
          </span>{' '}
          immediately — each has already spent this much or more this period.
          They will run again when the limit is raised or the period turns over.
        </p>
      ) : null}
      {setDefault.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {setDefault.error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={setDefault.isPending || unchanged}>
          {setDefault.isPending ? 'Saving…' : 'Save default'}
        </Button>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * The organization's runs-at-once default (spec 117 US4): a whole number from 1 to the
 * ceiling, validated here with the range stated, as the service validates it.
 */
export function RunsDefaultEditor({
  current,
  fallback,
  ceiling,
  onDone,
}: {
  current: number | null;
  fallback: number;
  ceiling: number;
  onDone: () => void;
}) {
  const [draft, setDraft] = useState(String(current ?? fallback));
  const [refusal, setRefusal] = useState<string | null>(null);
  const set = useSetConcurrencyLimit(ceiling);
  const error = refusal ?? set.error?.message ?? null;

  return (
    <form
      aria-label="Runs default"
      noValidate
      className={EDITOR}
      onSubmit={(event) => {
        event.preventDefault();
        const parsed = parseConcurrencyLimit(draft, ceiling);
        if (!parsed.ok) {
          setRefusal(parsed.reason);
          return;
        }
        setRefusal(null);
        set.mutate(
          { workspaceId: null, value: parsed.value },
          { onSuccess: onDone },
        );
      }}
    >
      <p className="text-muted-foreground text-sm">
        How many runs every workspace without a custom limit may have at once.
        Runs over it wait their turn and start by themselves; lowering it never
        stops a run already going.
      </p>
      <label className="flex flex-col gap-1.5 text-sm font-medium">
        Runs per workspace
        <input
          type="number"
          min={1}
          max={ceiling}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setRefusal(null);
          }}
          aria-invalid={error !== null}
          className="border-input bg-background focus-visible:ring-ring h-9 w-24 rounded-md border px-3 font-normal tabular-nums focus-visible:ring-3 focus-visible:outline-none"
        />
      </label>
      <span className="text-muted-foreground text-xs">
        {`Allowed 1–${String(ceiling)}`}
      </span>
      {error ? (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={set.isPending}>
          {set.isPending ? 'Saving…' : 'Save default'}
        </Button>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
